import React from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Box, Flex, Icon } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { themeGet } from 'styled-system';

import Text from '@/components/Text';
import { getMilitaryTime, isSameDay, militaryTime } from '@/lib/date';
import { longDate } from '@/lib/date/format';
import { SCREEN_SIZE } from '@/lib/theme/breakPoints';
import Flight from '@/propTypes/Flight';
import { getTransitDuration } from '@/utils/mapDurationFromFlights';
import OvernightBadge from '../OvernightBadge';

const StyledFlex = styled(Flex)`
  ${({ transit }) =>
    transit &&
    css`
      position: relative;
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 8px;
        height: 100%;
        border-right: 1px solid #aaa;
      }
    `};
`;

const StyledDetailsFlex = styled(Flex)`
  margin-left: 0;
  margin-right: 0;
  z-index: 0;
  padding-top: ${themeGet('space.4')};
  padding-bottom: ${themeGet('space.4')};
  width: 100%;
  ${SCREEN_SIZE.SM} {
    padding-top: ${themeGet('space.6')};
    padding-bottom: ${themeGet('space.8')};
    border-top: 1px solid ${themeGet('colors.border')};
    order: 1;
  }
`;

const labelColumnWidth = '96px';

const formatCarrierDescription = (carrierDescription) => {
  const [operatedBy, carrier] = carrierDescription.split(':');
  return (
    <Flex mt={[1, null, 0]} alignItems={[null, null, 'center']}>
      <Box width={labelColumnWidth} ml="8">
        <Text medium bold>{`${operatedBy}`}</Text>
      </Box>
      <Box flex={1} ml={2}>
        <Text medium>{carrier}</Text>
      </Box>
    </Flex>
  );
};

const FlightDetails = React.memo(({ flights }) => (
  <StyledDetailsFlex flexDirection="column">
    {flights &&
      flights.map((flight, index) => {
        const nextDay = !isSameDay(
          new Date(flight.departure.date),
          new Date(flight.arrival.date),
        );

        const duration = getMilitaryTime(flight.duration);
        const transit =
          index > 0
            ? getTransitDuration(flights[index - 1], flight)
            : undefined;

        return (
          <Flex
            key={`${flight.flightNumber}-${flight.origin?.code}-${flight.destination?.code}`}
            flexDirection="column"
          >
            {transit && (
              <StyledFlex py={4} transit>
                <Flex alignItems="center" backgroundColor="white">
                  <Box zIndex="1" height={24} backgroundColor="white">
                    <Icon
                      name="circleBorder"
                      size={16}
                      color="icon.inclusion"
                    />
                  </Box>
                  <Box ml="4">
                    <Text medium>
                      Transit in{' '}
                      {`${flight.origin.name} for ${transit.hour} hrs ${transit.minute} min`}
                    </Text>
                  </Box>
                </Flex>
              </StyledFlex>
            )}
            <StyledFlex
              flexDirection="column"
              transit={!transit && flights.length > 1}
            >
              <Flex>
                <Flex alignItems="center" backgroundColor="white">
                  <Box zIndex="1" height={24} backgroundColor="white">
                    <Icon name="flightRight" color="icon.body" size={16} />
                  </Box>
                  <Box width={labelColumnWidth} ml="4">
                    <Text medium bold>{`Flight ${index + 1}`}</Text>
                  </Box>
                </Flex>
                <Box flex={1} ml={2}>
                  <Text medium>{flight.flightNumber}</Text>
                </Box>
              </Flex>
              <Flex mt={[1, null, 0]} alignItems={[null, null, 'center']}>
                <Box width={labelColumnWidth} ml="8">
                  <Text medium bold>
                    Departs
                  </Text>
                </Box>
                <Box flex={1} ml={2}>
                  <Text medium>
                    {`${longDate(flight.departure.date)}, ${militaryTime(
                      flight.departure.time,
                    )},
                  ${flight.origin.name}`}
                  </Text>
                </Box>
              </Flex>
              <Flex mt={[1, null, 0]} alignItems={[null, null, 'center']}>
                <Box width={labelColumnWidth} ml="8">
                  <Text medium bold>
                    Arrives
                  </Text>
                </Box>
                <Box flex={1} ml={2}>
                  <Text medium>
                    {`${longDate(flight.arrival.date)}, ${militaryTime(
                      flight.arrival.time,
                    )},
                ${flight.destination.name}`}
                  </Text>
                  {nextDay && <OvernightBadge />}
                </Box>
              </Flex>
              <Flex mt={[1, null, 0]} alignItems={[null, null, 'center']}>
                <Box width={labelColumnWidth} ml="8">
                  <Text medium bold>
                    Cabin
                  </Text>
                </Box>
                <Box flex={1} ml={2}>
                  <Text medium>{flight.flightClass}</Text>
                </Box>
              </Flex>
              <Flex mt={[1, null, 0]} alignItems={[null, null, 'center']}>
                <Box width={labelColumnWidth} ml="8">
                  <Text medium bold>
                    Duration
                  </Text>
                </Box>
                <Box flex={1} ml={2}>
                  <Text
                    medium
                  >{`${duration.hour} hr ${duration.minute} min`}</Text>
                </Box>
              </Flex>
              {flight.carrierDescription &&
                formatCarrierDescription(flight.carrierDescription)}
            </StyledFlex>
          </Flex>
        );
      })}
  </StyledDetailsFlex>
));

FlightDetails.propTypes = {
  flights: PropTypes.arrayOf(PropTypes.shape(Flight)),
};

export default FlightDetails;
